/* eslint-disable max-len */
import { isEmpty, concat } from 'lodash';
import Moment from 'moment';
import { extendMoment } from 'moment-range';

import config from '../../../config';
import { LOCATIONS_ALL_ID } from './containers/BrandBarber/constants';
import { prepareSchedule } from '../../common/helpers/schedules';

const moment = extendMoment(Moment);

const hardcodedInfinityDate = '2100-01-01T00:00:00.000Z';

export const CONFLICT_TYPE = {
  SHOP_IS_ROUTINELY_CLOSED: 1,
  BARBER_WORKS_AT_ANOTHER_SHOP: 2,
};

/**
 * Check if start-end time periods overlap
 * @param {Object.<{ startTime: string, endTime: string }>} item1
 * @param {Object.<{ startTime: string, endTime: string }>} item2
 * @return {boolean}
 */
const timesOverlap = (item1, item2) => {
  const item1StartDateTime = moment(item1.startTime, config.timeFormat);
  const item1EndDateTime = moment(item1.endTime, config.timeFormat);
  const item2StartDateTime = moment(item2.startTime, config.timeFormat);
  const item2EndDateTime = moment(item2.endTime, config.timeFormat);

  const item1TimeRange = moment.range(item1StartDateTime, item1EndDateTime);
  const item2TimeRange = moment.range(item2StartDateTime, item2EndDateTime);

  return item1TimeRange.overlaps(item2TimeRange);
};

/**
 * Checks if schedule item for a particular week day has conflicts with schedule
 * @param {Object} otherSchedule - schedule to check conflicts with
 * @param {Object} scheduleWorkday - schedule item for a particular week day
 * @param {boolean} scheduleWorkday.enabled is schedule item enabled or not
 * @param {number} scheduleWorkday.weekDay week day (1(Mon)-7(Sun))
 * @param {string} scheduleWorkday.startTime formatted start time, example '9:30 am'
 * @param {string} scheduleWorkday.endTime formatted end time, example '6:30 pm'
 * @param {(string | null)} scheduleStartDate - item start date, example '2022-10-31T00:00:00.000Z'
 * @param {(string | null)} scheduleEndDate - item end date, example '2022-10-31T00:00:00.000Z'
 * @param {boolean} conflictWithEnabled - mark day as conflicted if other schedule has this same day enabled (or disabled if this param is false)
 * @return {boolean}
 */
const isScheduleWorkdayInConflictWithSchedule = ({ otherSchedule, scheduleWorkday, scheduleStartDate, scheduleEndDate, conflictWithEnabled }) => {
  if (!scheduleStartDate || !otherSchedule.startDate) {
    return false;
  }

  const scheduleRange =
    moment.range(
      moment(scheduleStartDate),
      moment(scheduleEndDate || hardcodedInfinityDate)
    );
  const otherScheduleRange =
    moment.range(
      moment(otherSchedule.startDate),
      moment(otherSchedule.endDate || hardcodedInfinityDate)
    );

  if (!scheduleRange.overlaps(otherScheduleRange)) {
    return false;
  }

  const doWorkDaysConflict = otherSchedule.workdays.some((otherScheduleWorkDay) => {
    if (otherScheduleWorkDay.weekDay !== scheduleWorkday.weekDay) {
      return false;
    }

    if (conflictWithEnabled) {
      return otherScheduleWorkDay.enabled && timesOverlap(scheduleWorkday, otherScheduleWorkDay);
    }

    return !otherScheduleWorkDay.enabled;
  });

  return doWorkDaysConflict;
};

/**
 * Returns array of items barber's schedule work day in conflict with
 * (warn if barber is set to be working in more than one shop at the same time)
 * @param {array} otherShopsSchedules - barber schedules in all shops except target shop
 * @param scheduleWorkday - target shop schedule work day
 * @param scheduleWorkday.enabled is schedule item enabled or not
 * @param scheduleWorkday.weekDay scheduleItem.weekDay
 * @param scheduleWorkday.startTime formatted start time, example '9:30 am'
 * @param scheduleWorkday.endTime formatted end time, example '6:30 pm'
 * @param {(string | null)} scheduleStartDate - target shop schedule start date, example '2022-10-31T00:00:00.000Z'
 * @param {(string | null)} scheduleEndDate - target shop schedule end date, example '2022-10-31T00:00:00.000Z'
 * @param {Object} shopNamesById - barber shops names info
 * @return {array} { shopId, shopName, conflictType }[];
 */
export const getScheduleWorkdayConflictsWithOthers =
  ({ otherShopsSchedules, scheduleWorkday, scheduleStartDate, scheduleEndDate, shopNamesById }) => {
    if (isEmpty(otherShopsSchedules) || !scheduleWorkday.enabled) {
      return [];
    }

    const result = [];

    otherShopsSchedules.forEach((shopSchedule) => {
      if (['current', 'future'].some(scheduleType =>
        isScheduleWorkdayInConflictWithSchedule({
          otherSchedule: shopSchedule.schedules[scheduleType],
          scheduleWorkday,
          scheduleStartDate,
          scheduleEndDate,
          conflictWithEnabled: true,
        })
      )) {
        result.push({
          shopId: shopSchedule.shopId,
          shopName: shopNamesById[shopSchedule.shopId],
          conflictType: CONFLICT_TYPE.BARBER_WORKS_AT_ANOTHER_SHOP,
        });
      }
    });

    return result;
  };

/**
 * Returns array of items barber's schedule item in conflict with
 * (warn if barber is set to be working in a shop when this shop is set to be closed)
 * @param {object} shop - shop in question
 * @param scheduleWorkday - barber's schedule workday in the same shop
 * @param scheduleWorkday.enabled is schedule workday enabled or not
 * @param scheduleWorkday.weekDay scheduleItem.weekDay
 * @param scheduleWorkday.startTime formatted start time, example '9:30 am'
 * @param scheduleWorkday.endTime formatted end time, example '6:30 pm'
 * @param {(string | null)} scheduleStartDate - scheduleWorkday's schedule start date, example '2022-10-31T00:00:00.000Z'
 * @param {(string | null)} scheduleEndDate - scheduleWorkday's schedule end date, example '2022-10-31T00:00:00.000Z'
 * @return {array} { shopId, scheduleId }[];
 */
export const getScheduleWorkdayConflictsWithShopSchedule = ({ shop, scheduleWorkday, scheduleStartDate, scheduleEndDate }) => {
  const preparedShop = {
    ...shop,
    schedules: prepareSchedule(shop.schedules),
  };

  if (!shop.schedules || !shop.schedules.length || !scheduleWorkday.enabled) {
    return [];
  }

  if (['current', 'future'].some(scheduleType =>
    isScheduleWorkdayInConflictWithSchedule({
      otherSchedule: preparedShop.schedules[scheduleType],
      scheduleWorkday,
      scheduleStartDate,
      scheduleEndDate,
      conflictWithEnabled: false,
    })
  )) {
    return [{
      shopId: shop.id,
      shopName: shop.name,
      conflictType: CONFLICT_TYPE.SHOP_IS_ROUTINELY_CLOSED,
    }];
  }

  return [];
};

/**
 * Get conflicts descriptions for work days in barber's schedule for a shop
 * @param params
 * @param {array} params.otherShopsSchedules - barber schedules in all shops except target shop
 * @param {Object} params.shopSchedule - barber schedule in target shop
 * @param {Object.<id: string, name: string, schedules: array>} params.shop - shop info
 * @param {Object} shopNamesById - barber shops names info
 * @return {Object.<{ day: string, conflicts: Array }>[]} - daysOn with conflicts added for each day
 */
export const getBarberShopScheduleWithConflicts = ({ otherShopsSchedules, shopSchedule, shopNamesById, shop }) => {
  if (!shopSchedule || !shopSchedule.workdays) {
    return [];
  }

  return shopSchedule.workdays.map(scheduleWorkday => ({
    ...scheduleWorkday,
    conflicts: concat(
      getScheduleWorkdayConflictsWithOthers({
        otherShopsSchedules,
        scheduleWorkday,
        scheduleStartDate: shopSchedule.startDate,
        scheduleEndDate: shopSchedule.endDate,
        shopNamesById,
      }),
      getScheduleWorkdayConflictsWithShopSchedule({
        shop,
        scheduleWorkday,
        scheduleStartDate: shopSchedule.startDate,
        scheduleEndDate: shopSchedule.endDate,
      }),
    ),
  }));
};

/**
 * Get conflicts descriptions if day overlaps with schedule item (either current or future)
 * @param {Object} props
 * @param {Object.<{ day: string, startTime: string | null, endTime: string | null }>} props.dayOn - day to check conflicts for
 * @param {Object.<startDate: string | null, endDate: string | null, workdays: array>} props.scheduleItem - schedule item to check conflicts against
 * @param {boolean} props.conflictWithEnabled - count day as conflicted if schedule day is enabled (or disabled)
 * @param {Object.<{ id: string, name: string }>} props.shop - schedule's shop info
 * @return {Array.<{ shopName: string, shopId: string, conflictType: number, startTime: string | null, endTime: string | null }>} - array of conflicts (if any);
 */
export const getDayConflictsWithScheduleItem = ({ dayOn, scheduleItem, conflictWithEnabled, shop }) => {
  if (!scheduleItem.startDate) {
    return [];
  }

  const momentDateItem = moment(dayOn.day, config.dateFormatISO);

  const scheduleRange =
    moment.range(
      moment(scheduleItem.startDate),
      moment(scheduleItem.endDate || hardcodedInfinityDate)
    );

  if (!scheduleRange.contains(momentDateItem)) {
    return [];
  }

  const result = [];

  scheduleItem
    .workdays
    .forEach((workDay) => {
      if (momentDateItem.isoWeekday() !== workDay.weekDay) {
        return;
      }

      const conflictsWithWholeDay = conflictWithEnabled ? workDay.enabled : !workDay.enabled;
      if (!conflictsWithWholeDay) {
        return;
      }

      if (conflictWithEnabled && dayOn.startTime && dayOn.endTime) {
        if (!timesOverlap(dayOn, workDay)) {
          return;
        }

        result.push({
          shopName: shop.name,
          shopId: shop.id,
          startTime: workDay.startTime,
          endTime: workDay.endTime,
          conflictType: CONFLICT_TYPE.BARBER_WORKS_AT_ANOTHER_SHOP,
        });

        return;
      }

      if (conflictWithEnabled) {
        result.push({
          shopName: shop.name,
          shopId: shop.id,
          startTime: workDay.startTime,
          endTime: workDay.endTime,
          conflictType: CONFLICT_TYPE.BARBER_WORKS_AT_ANOTHER_SHOP,
        });
      } else {
        result.push({
          shopName: shop.name,
          shopId: shop.id,
          conflictType: CONFLICT_TYPE.SHOP_IS_ROUTINELY_CLOSED,
        });
      }
    });

  return result;
};

/**
 * Get conflicts description if day overlaps with schedule (either barber's or shop's)
 * @param {Object} props
 * @param {Object.<{ day: string, startTime: string | null, endTime: string | null }>} props.dayOn - day to check conflicts for
 * @param {Object.<{ current: Object, future: Object }>} props.schedule - schedule to check conflicts against
 * @param {boolean} props.conflictWithEnabled - count day as conflicted if schedule day is enabled (or disabled)
 * @param {Object.<{ id: string, name: string }>} props.shop - schedule's shop info
 * @return {Array.<{ shopName: string, shopId: string, conflictType: number, startTime: string | null, endTime: string | null }>} - array of conflicts (if any)
 */
export const getDayConflictsWithSchedule = ({ dayOn, schedule, conflictWithEnabled, shop }) =>
  concat(
    getDayConflictsWithScheduleItem(
      { dayOn, scheduleItem: schedule.current, conflictWithEnabled, shop }
    ),
    getDayConflictsWithScheduleItem(
      { dayOn, scheduleItem: schedule.future, conflictWithEnabled, shop }
    )
  );

const isSameDay = (day1, day2) => moment(day1, config.dateFormatISO).isSame(moment(day2, config.dateFormatISO), 'day');

/**
 * Given conflicts of a day ON with barber's schedule in another shop, adjust them to take into account barber's days ON and OFF in that other shop
 * @param {Object} props
 * @param {Object.<{ day: string, startTime: string | null, endTime: string | null }>} props.dayOn - day to check conflicts for
 * @param {Array.<{ shopName: string, shopId: string, conflictType: number, startTime: string | null, endTime: string | null }>} props.conflicts - calculated conflicts with barber's shop schedule
 * @param {Array.<{ day: string, startTime: string, endTime: string }>} props.scheduleDaysOn - barber's days ON in another shop
 * @param {Array.<{ day: string }>} props.scheduleDaysOff - barber's days OFF in another shop
 * @param {Object.<{ id: string, name: string }>} props.shop - schedule's shop info
 * @return {Array.<{ shopName: string, shopId: string, conflictType: number, startTime: string | null, endTime: string | null }>} - adjusted array of conflicts
 */
const adjustDayConflictsForDaysOn = ({ dayOn, conflicts, scheduleDaysOn, scheduleDaysOff, shop }) => {
  // 1. if dayOn has a conflict with barber's schedule in another shop, but the day is marked as a day off for that shop - remove that conflict
  const result = conflicts.filter(conflict => !(
    conflict.shopId === shop.id
      && conflict.conflictType === CONFLICT_TYPE.BARBER_WORKS_AT_ANOTHER_SHOP
      && !!scheduleDaysOff
      && scheduleDaysOff.some(dayOff => isSameDay(dayOff.day, dayOn.day))
  ));

  if (!!scheduleDaysOn && scheduleDaysOn.length) {
    // explicit day on in a shop _overrides_ regular work hours if barber is also set to work on this day in regular schedule
    // explicit day on also overrides explicit day off in the same shop for the same day
    const dayOnInAnotherShop = scheduleDaysOn.find(scheduleDayOn => isSameDay(scheduleDayOn.day, dayOn.day));

    const conflictWithScheduleIndex =
      result.findIndex(conflict => conflict.shopId === shop.id && conflict.conflictType === CONFLICT_TYPE.BARBER_WORKS_AT_ANOTHER_SHOP);
    const hasConflictWithSchedule = conflictWithScheduleIndex >= 0;

    const dayOnHasTime = dayOn.startTime && dayOn.endTime;
    const hasConflictWithScheduleDayOn = !!dayOnInAnotherShop && (!dayOnHasTime || timesOverlap(dayOn, dayOnInAnotherShop));
    const conflictWithScheduleDayOn = hasConflictWithScheduleDayOn ? {
      shopName: shop.name,
      shopId: shop.id,
      startTime: dayOnInAnotherShop.startTime,
      endTime: dayOnInAnotherShop.endTime,
      conflictType: CONFLICT_TYPE.BARBER_WORKS_AT_ANOTHER_SHOP,
    } : null;

    // add/replace conflict for scheduled day with the conflict for shedule's day on
    if (hasConflictWithScheduleDayOn) {
      result.splice(hasConflictWithSchedule ? conflictWithScheduleIndex : result.length, 1, conflictWithScheduleDayOn);
    } else if (!!dayOnInAnotherShop && hasConflictWithSchedule) {
      result.splice(conflictWithScheduleIndex, 1);
    }
  }

  return result;
};

/**
 * Get conflicts descriptions if day overlaps with barber's schedules
 * @param {Object.<{ day: string, startTime: string | null, endTime: string | null }>} dayOn - day to check conflicts for
 * @param {Array.<{shopId: string, schedules: array}>} barberShops - brand barber's data for shops
 * @param {Object} shopNamesById - barber shops names info
 * @return {Array.<{ shopName: string, shopId: string, conflictType: number, startTime: string | null, endTime: string | null }>} - array of conflicts (if any);
 */
export const getDayConflictsWithBarberSchedules = (dayOn, barberShops, shopNamesById) => {
  let conflicts = [];

  barberShops.forEach((barberShop) => {
    const shop = {
      id: barberShop.shopId,
      name: shopNamesById[barberShop.shopId],
    };

    let conflictsWithBarberShop = getDayConflictsWithSchedule(
      { dayOn, schedule: barberShop.schedules, conflictWithEnabled: true, shop }
    );

    conflictsWithBarberShop = adjustDayConflictsForDaysOn({
      dayOn,
      conflicts: conflictsWithBarberShop,
      scheduleDaysOn: barberShop.days_on,
      scheduleDaysOff: barberShop.days_off,
      shop,
    });

    conflicts = concat(conflicts, conflictsWithBarberShop);
  });

  return conflicts;
};

/**
 * Get conflicts descriptions if day overlaps with shop's schedule
 * @param {Object.<{ day: string, startTime: string | null, endTime: string | null }>} dayOn - day to check conflicts for
 * @param {Object.<id: string, name: string, schedules: array>} shop - shop info
 * @return {Array.<{ shopName: string, shopId: string, conflictType: number, startTime: string | null, endTime: string | null }>} - array of conflicts (if any);
 */
export const getDayConflictsWithShopSchedule = (dayOn, shop) => {
  const preparedShop = {
    ...shop,
    schedules: prepareSchedule(shop.schedules),
  };

  return getDayConflictsWithSchedule({
    dayOn,
    schedule: preparedShop.schedules,
    conflictWithEnabled: false,
    shop: preparedShop,
  });
};

/**
 * Get conflicts descriptions for a day on
 * @param params
 * @param {Object.<{ day: string, startTime: string | null, endTime: string | null }>} params.dayOn - day to check conflicts for
 * @param {Array.<{shopId: string, schedules: array}>} barberShops - brand barber's data for shops
 * @param {Object.<id: string, name: string, schedules: array>} params.shop - shop info
 * @param {Object} shopNamesById - barber shops names info
 * @return {Object.<{ day: string, conflicts: Array }>} - dayOn with conflicts added
 */
export const getDayOnWithConflicts = ({ dayOn, barberShops, shop, shopNamesById }) => ({
  ...dayOn,
  conflicts: concat(
    getDayConflictsWithBarberSchedules(dayOn, barberShops, shopNamesById),
    getDayConflictsWithShopSchedule(dayOn, shop)
  ),
});

/**
 * Get conflicts descriptions for days on
 * @param params
 * @param {Array.<{ day: string, startTime: string | null, endTime: string | null }>} params.daysOn - days to check conflicts for
 * @param {Object} params.brandBarber - brand barber form data object
 * @param {Object.<id: string, name: string, schedules: array>} params.shop - shop info
 * @param {Object} shopNamesById - barber shops names info
 * @return {Object.<{ day: string, conflicts: Array }>[]} - daysOn with conflicts added for each day
 */
export const getDaysOnWithConflicts = ({ daysOn, brandBarber, shop, shopNamesById }) => {
  const barberShops = Object.values(brandBarber)
    .filter(shopBarber => shopBarber.shopId !== shop.id && shopBarber.shopId !== LOCATIONS_ALL_ID);

  return daysOn.map(dayOn =>
    getDayOnWithConflicts({ dayOn, barberShops, shop, shopNamesById })
  );
};

/**
 * Get possible conflicts descriptions for a date in "add a day on" calendar
 * @param params
 * @param {Date} params.calendarDay - day in a calendar
 * @param {Object} params.brandBarber - brand barber form data object
 * @param {Object.<id: string, name: string, schedules: array>} params.shop - shop info
 * @param {Object} shopNamesById - barber shops names info
 * @return {Object.<{ day: string, conflicts: Array }>} - day with conflicts added
 */
export const getCalendarDayWithConflicts = ({ calendarDay, brandBarber, shop, shopNamesById }) => {
  const barberShops = Object.values(brandBarber)
    .filter(shopBarber => shopBarber.shopId !== shop.id && shopBarber.shopId !== LOCATIONS_ALL_ID);

  const dayOn = { day: moment(calendarDay).format(config.dateFormatISO) };

  return {
    ...getDayOnWithConflicts({ dayOn, barberShops, shop, shopNamesById }),
    day: calendarDay,
  };
};
